import {
  Box,
  CheckmarkSolidIcon,
  Typography
} from "@dealer-center/core-components";

export interface SaveConfirmationProps {
  count: number;
}

const SUCCESS_COLOR = "#07bc0c";
const CONFIRMATION_Z_INDEX = 9999;

/**
 * Coalesced, live-updating save confirmation surface.
 *
 * Unlike the Interstate toaster (which mints a new toast id per call and cannot
 * update in place), this single element re-renders instantly as `count` changes,
 * so a burst of saves shows one confirmation reading "{count} saved" rather than
 * a stack of individual toasts.
 *
 * It is positioned to match the toaster's native anchor (top-right on desktop,
 * top-center on small screens) and is marked aria-hidden because the spoken
 * confirmation is handled by a separate, debounced polite live region in the
 * parent so screen readers hear a single announcement per burst.
 */
export const SaveConfirmation = ({ count }: SaveConfirmationProps) => {
  if (count <= 0) return null;

  return (
    <Box
      aria-hidden="true"
      data-testid="save-confirmation"
      sx={{
        position: "fixed",
        zIndex: CONFIRMATION_Z_INDEX,
        top: { xs: 16, sm: 72 },
        right: { xs: "auto", sm: 16 },
        left: { xs: "50%", sm: "auto" },
        transform: { xs: "translateX(-50%)", sm: "none" },
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1.5,
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        boxShadow:
          "0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e0e0e0"
      }}
    >
      <CheckmarkSolidIcon
        fontSize="1.5em"
        stroke={SUCCESS_COLOR}
        title="Saved"
      />
      <Typography variant="body-md">{count} saved</Typography>
    </Box>
  );
};
